import { useCallback, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Briefcase, UserRoundCheck, Mail, Settings, LogOut, Loader2, Users } from "lucide-react";
import { Seo } from "../../components/site/Seo";
import { Logo } from "../../components/site/Logo";
import { useAuth } from "../../components/admin/AuthContext";
import { api } from "../../lib/api";
import { RecordsView } from "../../components/admin/RecordsView";
import { SettingsPanel } from "../../components/admin/SettingsPanel";
import { TeamPanel } from "../../components/admin/TeamPanel";
import { ADMIN_KINDS } from "../../components/admin/adminConfig";

const TABS = [
  ["overview", "Overview", LayoutDashboard], ["enquiries", "Business Requests", Briefcase],
  ["applications", "Expert Applications", UserRoundCheck], ["messages", "Contact Messages", Mail],
  ["team", "Team & Access", Users, true], ["settings", "Settings", Settings, true],
];

const Overview = ({ data, go }) => (
  <div className="grid gap-6 xl:grid-cols-3" data-testid="admin-overview">
    {Object.entries(ADMIN_KINDS).map(([k, cfg]) => {
      const d = data?.[k];
      return (
        <section key={k} className="rounded-md border border-slate-200 bg-white p-6">
          <div className="flex items-baseline justify-between">
            <h2 className="font-display text-lg font-semibold text-[#0B1220]">{cfg.label}</h2>
            <button type="button" onClick={() => go(k)} className="text-sm font-semibold text-[#2563EB]" data-testid={`overview-open-${k}`}>Open</button>
          </div>
          <p className="mt-4 font-display text-4xl font-semibold text-[#0B1220]" data-testid={`overview-total-${k}`}>{d ? d.total : "—"}</p>
          <p className="text-xs text-slate-500">active records{d ? ` · ${d.archived} archived` : ""}</p>
          <ul className="mt-6 space-y-2 text-sm">
            {cfg.statuses.map((s) => (
              <li key={s} className="flex justify-between border-b border-slate-100 pb-2 last:border-0"><span className="text-slate-600">{s}</span><span className="font-medium text-[#0B1220]">{d ? d.by_status[s] : "—"}</span></li>
            ))}
          </ul>
        </section>
      );
    })}
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const loadOverview = useCallback(() => { api.get("/admin/overview").then(({ data }) => setOverview(data)).catch(() => {}); }, []);
  useEffect(() => { if (user) loadOverview(); }, [user, loadOverview]);

  if (user === null) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!user) return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;

  const isAdmin = user.role === "admin";
  const tabs = TABS.filter((t) => isAdmin || !t[3]);
  const current = tabs.find((t) => t[0] === tab) || tabs[0];
  return (
    <div className="min-h-screen bg-[#F4F6F8] lg:grid lg:grid-cols-[248px_1fr]" data-testid="admin-dashboard">
      <Seo title="Admin" description="Restricted administrator dashboard." noindex />
      <aside className="flex flex-col bg-[#0B1220] text-white lg:min-h-screen">
        <div className="px-6 py-6"><Logo dark testId="admin-logo" /></div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-col lg:pb-0" aria-label="Admin">
          {tabs.map(([k, l, Icon]) => (
            <button key={k} type="button" onClick={() => setTab(k)} className={`flex shrink-0 items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${tab === k ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`} data-testid={`admin-tab-${k}`}>
              <Icon className="h-4 w-4" /> {l}
              {overview?.[k] && <span className="ml-auto rounded-sm bg-white/10 px-1.5 text-[11px]">{overview[k].by_status[ADMIN_KINDS[k].statuses[0]]}</span>}
            </button>
          ))}
        </nav>
        <div className="mt-auto hidden border-t border-white/10 px-6 py-5 lg:block">
          <p className="truncate text-xs text-slate-400" data-testid="admin-user-email">{user.email}</p>
          <p className="mt-1 text-[11px] uppercase tracking-[0.14em] text-slate-500" data-testid="admin-user-role">{user.role}</p>
          <button type="button" onClick={logout} className="mt-3 flex items-center gap-2 text-sm text-slate-300 hover:text-white" data-testid="admin-logout-button"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      </aside>
      <main className="min-w-0 p-5 lg:p-10">
        <div className="mb-8 flex items-center justify-between gap-4">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-[#0B1220]">{current[1]}</h1>
          <button type="button" onClick={logout} className="btn-secondary h-9 px-3 text-sm lg:hidden" data-testid="admin-logout-button-mobile"><LogOut className="h-4 w-4" /></button>
        </div>
        {current[0] === "overview" && <Overview data={overview} go={setTab} />}
        {ADMIN_KINDS[current[0]] && <RecordsView key={current[0]} kind={current[0]} canManage={isAdmin} onChanged={loadOverview} />}
        {current[0] === "team" && isAdmin && <TeamPanel />}
        {current[0] === "settings" && isAdmin && <SettingsPanel />}
      </main>
    </div>
  );
}
